/**
 * Purchase packs: optional packaging a product is bought in, e.g.
 * Card = 12 tablet. Factors are in the product's base unit (`scale`).
 *
 * Stock is counted in base units, so a purchase line always carries its
 * quantity in those, whatever pack it was bought in.
 */

export interface Pack {
  name: string;
  unitsPerPack: number | string;
  isDefault: boolean;
}

export interface DerivedLine {
  quantity: number;
  price: number;
  unitPrice: number;
}

/** A pack of 1 is just the base unit, so it is not a pack. */
export const MIN_UNITS_PER_PACK = 2;

/**
 * Price is entered per pack, because that is how suppliers bill it. The
 * line total is the exact figure; the per-unit cost is derived from it,
 * never the other way round - 100 / 12 would round into a total that no
 * longer matches the invoice.
 */
export const derivePurchaseLine = (
  packQuantity: number,
  unitsPerPack: number,
  packPrice: number
): DerivedLine => {
  const quantity = packQuantity * unitsPerPack;
  const price = packPrice * packQuantity;
  return {
    quantity,
    price,
    unitPrice: quantity > 0 ? price / quantity : 0,
  };
};

/** The pack a product opens on in the purchase modal, if it has one. */
export const resolveDefaultPack = (packs: Pack[] | undefined) =>
  (packs || []).find((pack) => pack.isDefault);

/** Factor to use for a pack name; the base unit is 1. */
export const unitsForPack = (packs: Pack[] | undefined, name: string) => {
  const pack = (packs || []).find((item) => item.name === name);
  return pack ? Number(pack.unitsPerPack) : 1;
};

/**
 * Ticking one default clears the others - there can only ever be one.
 */
export const setDefaultPack = (packs: Pack[], index: number): Pack[] =>
  packs.map((pack, i) => ({ ...pack, isDefault: i === index }));

/** Rows the user never filled in are dropped rather than rejected. */
export const filledPacks = (packs: Pack[]): Pack[] =>
  packs.filter(
    (pack) => pack.name.trim() !== "" || String(pack.unitsPerPack) !== ""
  );

/** The first pack that would be rejected, if any. */
export const findInvalidPack = (packs: Pack[]): Pack | undefined =>
  filledPacks(packs).find(
    (pack) =>
      pack.name.trim() === "" || Number(pack.unitsPerPack) < MIN_UNITS_PER_PACK
  );

/** "5 Card × 12 tablet" for a packed line, plain base units otherwise. */
export const describeQuantity = (item: any): string => {
  const unit = item?.scale || "";
  if (!item?.packName) {
    return `${item?.quantity ?? 0} ${unit}`.trim();
  }
  return `${item?.packQuantity} ${item.packName} × ${item?.unitsPerPack} ${unit}`.trim();
};

/**
 * Edit a purchase line in place. Quantity and price are typed per pack
 * (per base unit for an unpacked line), exactly as in the Add Product
 * modal, so the raw text is kept while typing and the derived figures
 * (base-unit quantity, line total, per-unit cost) follow it.
 */
export const applyPurchaseLineEdit = (
  line: any,
  edit: { packQuantity?: string | number; packPrice?: string | number }
): any => {
  const next = { ...line, ...edit };
  const packQuantity = Number(next.packQuantity) || 0;
  const unitsPerPack = Number(next.unitsPerPack) || 1;
  const packPrice = Number(next.packPrice) || 0;
  return {
    ...next,
    ...derivePurchaseLine(packQuantity, unitsPerPack, packPrice),
  };
};

/** Turn any raw text left by editing back into numbers before saving. */
export const normalizePurchaseLine = (line: any): any => ({
  ...line,
  packQuantity: Number(line?.packQuantity) || 0,
  packPrice: Number(line?.packPrice) || 0,
  unitsPerPack: Number(line?.unitsPerPack) || 1,
});

/** A line that cannot be saved: nothing bought, or bought for nothing. */
export const isInvalidPurchaseLine = (line: any): boolean =>
  !(Number(line?.packQuantity) > 0) || !(Number(line?.packPrice) > 0);
